import { ProprietaryTimer } from '@signageos/common-types/dist/Device/Settings/DeviceSettings';
import { ProprietaryTimerType } from '@signageos/common-types/dist/Device/Timer/ProprietaryTimerType';
import { now as getCurrentMoment } from '@signageos/lib/dist/DateTime/dateTimeFactory';
import { getLastTimerEvent, getNextTimerEvent, ITimerEvent } from '@signageos/lib/dist/Timer/Power/powerTimerComputer';
import wait from '@signageos/lib/dist/Timer/wait';
import Debug from 'debug';
import { EventEmitter } from 'events';
import IProprietaryResolver, { TimerEvent, TimerListener } from './IProprietaryTimerResolver';
import { IProprietaryTimerStorage } from './ITimerStorage';

const debug = Debug('@signageos/front-display:Timer:ProprietaryTimerManager');

const getCurrentDate = () => getCurrentMoment().toDate();

export default class ProprietaryTimerResolver implements IProprietaryResolver<TimerEvent> {
	private readonly eventEmitter: EventEmitter = new EventEmitter();

	private hasStartedListening: boolean = false;
	private nextTimerOnTime: Date | null = null;
	private appletMuted: boolean = false;

	constructor(
		private timersStorage: IProprietaryTimerStorage,
		private readonly checkPeriod: number = 30 * 1e3,
		private now: () => Date = getCurrentDate,
		private appletLastEnabled: boolean = true,
	) {
		// Listeners: TIMER_ON, TIMER_OFF, APPLET_MUTE_ON, APPLET_MUTE_OFF.
		this.eventEmitter.setMaxListeners(4);
	}

	public getNextTimerOnTime(): Date | null {
		return this.nextTimerOnTime;
	}

	public on(event: TimerEvent, listener: TimerListener): this {
		this.eventEmitter.on(event, listener);

		if (!this.hasStartedListening) {
			this.hasStartedListening = true;
			this.timersChecking();
			// TODO Add cancelation mechanism and clean up after use.
		}

		return this;
	}

	private emit(event: TimerEvent) {
		this.eventEmitter.emit(event);
	}

	private async timersChecking() {
		while (true) {
			debug('check timers now');
			try {
				const now = this.now();
				const timers = await this.timersStorage.getShortTimers();
				await checkTimer(
					now,
					async () => timers,
					(event: TimerEvent) => {
						if (event === TimerEvent.TIMER_OFF || event === TimerEvent.APPLET_MUTE_ON) {
							const nextOnEvent = getNextTimerEvent(timers, now, 'ON');
							this.nextTimerOnTime = nextOnEvent ? timerEventToDate(nextOnEvent, now) : null;
						} else {
							this.nextTimerOnTime = null;
						}
						this.emit(event);
					},
					(state: boolean) => this.setLastAppletState(state),
					this.appletLastEnabled,
					{
						current: this.appletMuted,
						set: (state: boolean) => this.setAppletMuted(state),
					},
				);
			} catch (error) {
				console.error('timerChecking failed', error);
			}
			await wait(this.checkPeriod);
		}
	}
	private async setLastAppletState(lastState: boolean) {
		this.appletLastEnabled = lastState;
		return this.appletLastEnabled;
	}
	private async setAppletMuted(muted: boolean) {
		this.appletMuted = muted;
		return this.appletMuted;
	}
}

export function timerEventToDate(event: ITimerEvent, now: Date): Date {
	const [hours, minutes] = event.time.split(':').map(Number);
	const daysUntil = (event.weekDayNumber - now.getDay() + 7) % 7;
	const result = new Date(now);
	result.setDate(result.getDate() + daysUntil);
	result.setHours(hours, minutes, 0, 0);
	if (result <= now) {
		result.setDate(result.getDate() + 7);
	}
	return result;
}

export async function checkTimer(
	now: Date,
	getTimers: () => Promise<Record<ProprietaryTimerType, ProprietaryTimer>>,
	emit: (event: TimerEvent) => void,
	setLastAppletState: (state: boolean) => Promise<boolean>,
	appletLastEnabled: boolean,
	muteState?: { current: boolean; set: (state: boolean) => Promise<boolean> },
) {
	const timers = await getTimers();
	const lastTimerEvent = getLastTimerEvent(timers, now);
	if (lastTimerEvent == null) {
		return;
	}

	const isTimerOn = lastTimerEvent.type === 'ON';
	const displayOn = appletLastEnabled;
	const keepAppletRunning = timers[lastTimerEvent.timerType].keepAppletRunning ?? false;

	if (isTimerOn !== displayOn) {
		if (isTimerOn) {
			debug('emit timer on');
			await setLastAppletState(true);
			emit(TimerEvent.TIMER_ON);
		}

		if (!isTimerOn && !keepAppletRunning) {
			await setLastAppletState(false);
			debug('emit timer off');
			emit(TimerEvent.TIMER_OFF);
		}
	}

	if (muteState) {
		const shouldBeMuted = !isTimerOn && keepAppletRunning && displayOn;
		if (shouldBeMuted !== muteState.current) {
			await muteState.set(shouldBeMuted);
			if (shouldBeMuted) {
				debug('emit applet mute on');
				emit(TimerEvent.APPLET_MUTE_ON);
			} else {
				debug('emit applet mute off');
				emit(TimerEvent.APPLET_MUTE_OFF);
			}
		}
	}
}
